import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface CustomerKeyValueItem {
  label: string;
  value: ReactNode;
  /** Hide the row entirely when the value is empty (keeps lists honest). */
  hideWhenEmpty?: boolean;
}

interface CustomerKeyValueProps {
  items: CustomerKeyValueItem[];
  /** 'rows' = label left / value right (metadata); 'grid' = stacked pairs in
   * 1 → 2 columns (descriptive facts). Both are mobile-first. */
  layout?: 'rows' | 'grid';
  className?: string;
  'data-testid'?: string;
}

function isEmpty(v: ReactNode): boolean {
  return v === null || v === undefined || v === '' || v === '—';
}

/**
 * Definition list for customer facts. Replaces the ad-hoc `flex justify-between`
 * rows scattered through the pages with one component that keeps label
 * contrast readable (never light-grey labels), wraps long values instead of
 * clipping them, and reflows from a single column on phones.
 */
export function CustomerKeyValue({
  items,
  layout = 'rows',
  className = '',
  'data-testid': testId,
}: CustomerKeyValueProps) {
  const visible = items.filter((i) => !(i.hideWhenEmpty && isEmpty(i.value)));
  if (visible.length === 0) return null;

  if (layout === 'grid') {
    return (
      <dl
        data-testid={testId}
        className={cn('grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2', className)}
      >
        {visible.map((i) => (
          <div key={i.label} className="min-w-0">
            <dt className="text-xs font-medium text-customer-text-secondary">{i.label}</dt>
            <dd className="mt-0.5 break-words text-sm font-medium text-customer-text">
              {isEmpty(i.value) ? '—' : i.value}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <dl data-testid={testId} className={cn('divide-y divide-customer-border', className)}>
      {visible.map((i) => (
        <div
          key={i.label}
          className="flex items-start justify-between gap-4 py-2.5 text-sm first:pt-0 last:pb-0"
        >
          <dt className="shrink-0 text-customer-text-secondary">{i.label}</dt>
          <dd className="min-w-0 break-words text-right font-medium text-customer-text">
            {isEmpty(i.value) ? '—' : i.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
